import { useMemo } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { useCall } from '../contexts/CallContext';
import config from '../config';

/**
 * useCallLayout — single source of truth for call layout mode and responsive sizing.
 *
 * Returns the current layout mode (pip/fullscreen/split), device type,
 * and responsive PiP dimensions based on screen size.
 *
 * Layout mode logic:
 *   Mobile portrait + expanded → fullscreen
 *   Mobile landscape + expanded → split
 *   Desktop + expanded → split
 *   Everything else → pip
 */
const useCallLayout = () => {
  const theme = useTheme();
  const { activeCall } = useCall();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));       // < 600px
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600-900px
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));        // >= 900px
  const isLandscape = useMediaQuery('(orientation: landscape)');

  const isExpanded = activeCall?.isExpanded || false;
  const { pip: pipConfig } = config.call;

  const mode = useMemo(() => {
    if (!activeCall) return 'pip';
    if (!isExpanded) return 'pip';

    // Mobile portrait expanded → fullscreen
    if (isMobile && !isLandscape) return 'fullscreen';
    // Mobile landscape expanded → split
    if (isMobile && isLandscape) return 'split';
    // Desktop/tablet expanded → split
    return 'split';
  }, [activeCall, isExpanded, isMobile, isLandscape]);

  const pipSize = useMemo(() => {
    if (isMobile) return { width: pipConfig.widthMobile, height: pipConfig.heightMobile };
    if (isTablet) return { width: pipConfig.widthTablet, height: pipConfig.heightTablet };
    return { width: pipConfig.widthDesktop, height: pipConfig.heightDesktop };
  }, [isMobile, isTablet, pipConfig]);

  return {
    mode,
    isMobile,
    isTablet,
    isDesktop,
    isLandscape,
    pipSize,
    pipConfig,
  };
};

export default useCallLayout;
